import { useState, useRef, useEffect } from 'react';
import { getDevAuthToolbarData } from '../utils/devAuth';

interface DevUser {
  name: string;
  email: string;
  role: string;
}

interface DevUserGroup {
  label: string;
  users: DevUser[];
}

const DEV_USER_GROUPS: DevUserGroup[] = [
  {
    label: 'Users',
    users: [
      { name: 'Bubba Sparxx', email: 'bubba@sparxx.com', role: 'USER' },
      { name: 'Clown Fish', email: 'clownie@bertco.com', role: 'USER' },
      { name: 'Anen Ome', email: 'anen@bertco.com', role: 'USER' },
      { name: 'Nasty Reviewer', email: 'nasty@review.com', role: 'USER' },
    ],
  },
  {
    label: 'Venue Managers',
    users: [
      { name: 'Dom Carneros', email: 'dom@carneros.com', role: 'VENUE_ADMIN' },
      { name: 'Bill Whetstone', email: 'billy@whetstone.com', role: 'VENUE_ADMIN' },
    ],
  },
  {
    label: 'Site Admins',
    users: [
      { name: 'Site Adminster', email: 'siteadmin@winex.com', role: 'SITE_ADMIN' },
      { name: 'Site McAdmin', email: 'mcadmin@winex.com', role: 'SITE_ADMIN' },
    ],
  },
];

const SUPER_ADMIN_USER: DevUser = {
  name: 'Mitch Kahn',
  email: 'makahn99@gmail.com',
  role: 'SUPER_ADMIN',
};

interface UserDropdownProps {
  label: string;
  users: DevUser[];
  currentEmail: string | null;
  onSelect: (email: string) => void;
}

const UserDropdown = ({ label, users, currentEmail, onSelect }: UserDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isActive = users.some((u) => u.email === currentEmail);
  const selectedUser = users.find((u) => u.email === currentEmail);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-2 rounded text-sm border-2 flex items-center gap-1 ${
          isActive ? 'bg-green-500 text-white border-green-700' : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
        }`}
      >
        <span>{selectedUser ? selectedUser.name : label}</span>
        <span className="text-xs">▼</span>
      </button>
      {isOpen && (
        <div className="absolute top-full mt-1 bg-gray-50 border-2 border-gray-300 rounded shadow-lg z-50 min-w-[220px] left-0">
          {users.map((user) => (
            <button
              key={user.email}
              onClick={() => {
                onSelect(user.email);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0 ${
                user.email === currentEmail ? 'bg-green-100' : ''
              }`}
            >
              <div className="font-semibold text-sm">{user.name}</div>
              <div className="text-xs text-gray-600">{user.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const getCurrentUserDisplay = (email: string | null): { name: string; role: string } | null => {
  if (!email) return null;
  for (const group of DEV_USER_GROUPS) {
    const user = group.users.find((u) => u.email === email);
    if (user) return { name: user.name, role: user.role };
  }
  if (email === SUPER_ADMIN_USER.email) {
    return { name: SUPER_ADMIN_USER.name, role: SUPER_ADMIN_USER.role };
  }
  return null;
};

/**
 * Development authentication toolbar
 * Only shows in development mode when VITE_ENABLE_DEV_AUTH=true
 */
export const DevAuthToolbar = () => {
  const toolbarData = getDevAuthToolbarData();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!toolbarData) {
    return null;
  }

  const { currentUserEmail, setUser } = toolbarData;
  const currentUserDisplay = getCurrentUserDisplay(currentUserEmail);
  const isSuperAdminSelected = currentUserEmail === SUPER_ADMIN_USER.email;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-slate-300 border-b-2 border-slate-600 text-black h-16">
      <div className="flex items-center justify-between px-4 py-3 h-full">
        <div className="flex items-center gap-4">
          <span className="font-bold text-sm">🔧 DEV AUTH</span>
          <div className="text-sm">
            {currentUserDisplay ? (
              <span>
                Current: <span className="font-semibold">{currentUserDisplay.name}</span>
                <span className="text-gray-600 ml-1">({currentUserDisplay.role})</span>
                {currentUserEmail && (
                  <span className="text-gray-500 ml-2 text-xs">{currentUserEmail}</span>
                )}
              </span>
            ) : (
              <span className="text-gray-600">No Auth (Anonymous)</span>
            )}
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1 bg-primary-800 text-white rounded text-sm hover:bg-primary-900"
        >
          {isExpanded ? 'Hide' : 'Switch User'}
        </button>
      </div>

      {isExpanded && (
        <div className="absolute top-16 left-0 right-0 border-t border-yellow-600 p-4 bg-yellow-300 z-50">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => {
                setUser(null);
                setIsExpanded(false);
              }}
              className={`px-3 py-2 rounded text-sm border-2 ${
                !currentUserEmail
                  ? 'bg-red-500 text-white border-red-700'
                  : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
              }`}
            >
              Anonymous
            </button>

            {DEV_USER_GROUPS.map((group) => (
              <UserDropdown
                key={group.label}
                label={group.label}
                users={group.users}
                currentEmail={currentUserEmail}
                onSelect={(email) => {
                  setUser(email);
                  setIsExpanded(false);
                }}
              />
            ))}

            <button
              onClick={() => {
                setUser(SUPER_ADMIN_USER.email);
                setIsExpanded(false);
              }}
              className={`px-3 py-2 rounded text-sm border-2 ${
                isSuperAdminSelected
                  ? 'bg-primary-700 text-white border-primary-800'
                  : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
              }`}
            >
              Super Admin
            </button>
          </div>

          <div className="mt-3 text-xs text-gray-700">
            💡 This toolbar only appears in development mode. Changes take effect immediately for
            new API calls.
          </div>
        </div>
      )}
    </div>
  );
};